import { useEffect, useRef, useState } from 'react';

export type TimerCallbacks = {
  /** Callback fired every 1s while timer is running. */
  onTick?: () => void;
  /** Callback fired when timer finishes. */
  onFinish?: () => void;
};

export type Timer = {
  time: number;
  timer: NodeJS.Timer | undefined;
  isRunning: boolean;
  start: () => void;
  stop: () => void;
  restart: () => void;
  pause: () => void;
  resume: () => void;
  paused: boolean;
};

export interface UseTimerProps extends TimerCallbacks {
  /** Duration of the timer in seconds. */
  duration: number;
}

const useTimer = ({ duration, onTick, onFinish }: UseTimerProps): Timer => {
  const [time, setTime] = useState(duration);
  const [paused, setPause] = useState(false);
  const [timer, setTimer] = useState<ReturnType<typeof setInterval>>();
  const isRunning = Boolean(timer && time);
  const callbackRef = useRef<TimerCallbacks>();

  /**
   * Update callback ref on changes to callback props
   * to allow changes to reflect within setInterval's callback.
   */
  useEffect(() => {
    callbackRef.current = { onTick, onFinish };
  }, [onTick, onFinish]);

  /**
   * Starts a stopped timer.
   */
  const start = () => {
    setTime(duration);

    const timer = setInterval(() => {
      callbackRef.current?.onTick?.();

      setTime((prevTime) => {
        if (!paused) {
          const updatedTime = prevTime - 1;

          if (!updatedTime) {
            clearInterval(timer);
            callbackRef.current?.onFinish?.();
          }
          return updatedTime;
        }
        return time;
      });
    }, 1000);

    setTimer(timer);
  };

  const pause = () => setPause(true);
  const resume = () => setPause(false);

  /**
   * Stops a running timer.
   */
  const stop = () => {
    clearInterval(timer);
    setTimer(undefined);
  };

  /**
   * Restarts a running or finished timer.
   */
  const restart = () => {
    setTime(0);
    stop();
  };

  /**
   * Restarts the timer if time is 0.
   * This allows UI to reset visually prior to restarting.
   */
  const handleRestart = () => {
    if (time) {
      return;
    }

    start();
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(handleRestart, [timer]);

  /**
   * Cleanup by clearing interval on unmount.
   */
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => () => clearInterval(timer), []);

  return { time, timer, isRunning, start, stop, restart, pause, resume, paused };
};

export default useTimer;
